import { useEffect, useRef } from 'react';
import useAuthStore from '../store/auth';
import api from '../services/api';

const useActivityTracker = () => {
  const { isAuthenticated, updateActivity, logout } = useAuthStore();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      updateActivity();
      // Schedule proactive token refresh after 1.5 hours of activity
      scheduleTokenRefresh();
    };

    const scheduleTokenRefresh = () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      // Refresh token after 1.5 hours (90 minutes) of activity
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const response = await api.post('/auth/refresh');
          const { accessToken } = response.data;

          // Update stored token
          localStorage.setItem('accessToken', accessToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          // Update activity timestamp
          useAuthStore.getState().updateActivity();
        } catch (error) {
          console.error('Proactive token refresh failed:', error);
          // Don't logout here - let the normal error handling take care of it
        }
      }, 90 * 60 * 1000); // 90 minutes
    };

    // Add event listeners for user activity
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Listen for activity updates from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'lastActivity' && event.newValue) {
        // Another tab updated activity, update our local state
        const now = Date.now();
        const lastActivity = parseInt(event.newValue);
        const sessionTimeout = 2.5 * 60 * 60 * 1000; // 2.5 hours in milliseconds

        if ((now - lastActivity) > sessionTimeout) {
          logout();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Schedule initial token refresh
    scheduleTokenRefresh();

    // Check for inactivity every minute
    const checkInactivity = setInterval(() => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (lastActivity) {
        const now = Date.now();
        const sessionTimeout = 2.5 * 60 * 60 * 1000; // 2.5 hours in milliseconds

        if ((now - parseInt(lastActivity)) > sessionTimeout) {
          logout();
        }
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkInactivity);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [isAuthenticated, updateActivity, logout]);
};

export default useActivityTracker;